import uuid4 from "uuid4";

// In-memory mirror of the widget token. The thread id lives in both React state
// and the cache, and the token follows the same lifetime: it arrives with the
// thread id from dengine and is dropped when the thread is reset. Kept in
// module scope (not on the instance) because ChatCache is constructed freely.
const widgetTokenMemory: Record<string, string | null> = {};

export default class ChatCache {
  private localStorageId: string = "drastik_chat_cache";

  public addMessage(message: any, threadId: any, assistantId: any) {
    let cache = this.getAssistant(assistantId);
    cache.messages.push(message);
    // A null id never wipes a known thread: only remove() resets it. Callers
    // pass the id they captured at send time, which is null on a first turn.
    if (threadId) cache.threadId = threadId;

    this.set(assistantId, cache);
  }

  public getMessages(assistantId: any) {
    return this.getAssistant(assistantId)?.messages ?? [];
  }

  // Persist the thread id as soon as dengine assigns it (stream `run.started`),
  // so a widget that remounts mid-conversation resumes the same thread.
  public setThreadId(assistantId: any, threadId: any) {
    if (!threadId) return;

    let cache = this.getAssistant(assistantId);
    cache.threadId = threadId;
    this.set(assistantId, cache);
  }

  public getThreadId(assistantId: any) {
    return this.getAssistant(assistantId)?.threadId;
  }

  /**
   * Persist the widget token dengine minted for the current thread. Stored next
   * to the thread id (memory + localStorage) and cleared with it.
   */
  public setWidgetToken(assistantId: any, token: any) {
    if (!token) return;

    widgetTokenMemory[assistantId] = token;

    let cache = this.getAssistant(assistantId);
    cache.widgetToken = token;
    this.set(assistantId, cache);
  }

  public getWidgetToken(assistantId: any) {
    if (widgetTokenMemory[assistantId]) return widgetTokenMemory[assistantId];

    return this.getAssistant(assistantId)?.widgetToken ?? null;
  }

  // Patch a cached message in place (e.g. guest feedback on a bot reply).
  // Index comes from the rendered list; content match guards against the
  // rare state/cache drift (a streamed reply that never reached the cache).
  public updateMessage(
    assistantId: any,
    index: number,
    content: string,
    patch: any
  ) {
    let cache = this.getAssistant(assistantId);
    let target = cache.messages[index];

    if (!target || target.content !== content) {
      target = [...cache.messages]
        .reverse()
        .find((m: any) => m.content === content);
    }
    if (!target) return;

    Object.assign(target, patch);
    this.set(assistantId, cache);
  }

  public remove(assistantId: any) {
    let cache = this.getAssistant(assistantId);
    cache.messages = [];
    cache.threadId = null;
    cache.widgetToken = null;

    delete widgetTokenMemory[assistantId];

    this.set(assistantId, cache);
  }

  private set(assistantId: any, entry: any) {
    let cache = this.get();
    cache[assistantId] = entry;
    localStorage.setItem(this.localStorageId, JSON.stringify(cache));
  }

  private getAssistant(assistantId: any) {
    return this.initializeAndGet(assistantId)[assistantId];
  }

  private get() {
    let cache: any = localStorage.getItem(this.localStorageId);
    return JSON.parse(cache);
  }

  private initializeAndGet(assistantId: any) {
    this.initialize();

    let cache: any = localStorage.getItem(this.localStorageId);
    cache = JSON.parse(cache);

    if (!cache[assistantId])
      cache[assistantId] = { messages: [], threadId: null, widgetToken: null };

    localStorage.setItem(this.localStorageId, JSON.stringify(cache));

    return cache;
  }

  private initialize() {
    let cache: any = localStorage.getItem(this.localStorageId);

    if (!cache) localStorage.setItem(this.localStorageId, "{}");

    this.setFingerprintIfEmpty();
  }

  private setFingerprintIfEmpty() {
    let cache = this.get();

    if (!cache.fingerprint) {
      cache.fingerprint = uuid4();
      localStorage.setItem(this.localStorageId, JSON.stringify(cache));
    }
  }

  public getFingerprint() {
    this.initialize();
    let cache = this.get();
    return cache.fingerprint;
  }
}
